/**
 * Visual semantics for asset statuses. The status *values* come from the database; this only
 * decides how a value looks, by meaning, with a neutral fallback for anything unrecognised.
 */
export type Tone = 'available' | 'assigned' | 'repair' | 'retired' | 'neutral';

export function statusTone(status: string): Tone {
  const s = status.toLowerCase();
  if (/avail|stock|spare|ready/.test(s)) return 'available';
  if (/assign|issued|in use|allocated/.test(s)) return 'assigned';
  if (/repair|service|maint|fault/.test(s)) return 'repair';
  if (/retir|dispos|scrap|lost|stolen/.test(s)) return 'retired';
  return 'neutral';
}

export const toneVar: Record<Tone, { fg: string; soft: string }> = {
  available: { fg: 'var(--st-available)', soft: 'var(--st-available-soft)' },
  assigned: { fg: 'var(--st-assigned)', soft: 'var(--st-assigned-soft)' },
  repair: { fg: 'var(--st-repair)', soft: 'var(--st-repair-soft)' },
  retired: { fg: 'var(--st-retired)', soft: 'var(--st-retired-soft)' },
  neutral: { fg: 'var(--text-2)', soft: 'var(--surface-2)' },
};

const markVar: Record<Tone, string> = {
  available: 'var(--mk-available)',
  assigned: 'var(--mk-assigned)',
  repair: 'var(--mk-repair)',
  retired: 'var(--mk-retired)',
  neutral: 'var(--chart-neutral)',
};

/** Fill for chart marks (validated palette); text keeps using toneVar ink. */
export const statusColor = (status: string) => markVar[statusTone(status)];

/** Human wording for what a status means operationally. */
export function statusHint(status: string): string {
  switch (statusTone(status)) {
    case 'available':
      return 'In stock, ready to issue';
    case 'assigned':
      return 'Issued to an employee';
    case 'repair':
      return 'Out of service for repair';
    case 'retired':
      return 'Removed from service';
    default:
      return status;
  }
}
